import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Sun, ArrowRight, Loader2, ImageDown } from "lucide-react";
import { API, BACKEND_URL } from "@/lib/api";
import CardDialog from "@/components/CardDialog";
import SmartImage from "@/components/SmartImage";

export default function DailyVerse({ onExplore }) {
  const [verse, setVerse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cardOpen, setCardOpen] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/daily-verse`)
      .then(({ data }) => setVerse(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="mx-auto mt-12 flex max-w-3xl items-center justify-center gap-3 rounded-lg border border-border bg-card/60 p-8">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <span className="font-sans text-sm text-muted-foreground">Unrolling today's verse...</span>
      </div>
    );
  }
  if (!verse) return null;

  return (
    <motion.div
      data-testid="daily-verse"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7 }}
      className="mx-auto mt-12 grid max-w-3xl grid-cols-1 overflow-hidden rounded-lg border border-border bg-card shadow-[0_10px_40px_rgba(140,106,71,0.10)] sm:grid-cols-5"
    >
      {verse.image_url && (
        <div className="relative sm:col-span-2">
          <SmartImage src={`${BACKEND_URL}${verse.image_url}`} alt={verse.reference} className="h-40 w-full sm:h-full" imgClassName="object-cover" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 to-transparent sm:bg-gradient-to-r" />
        </div>
      )}
      <div className="p-7 sm:col-span-3">
        <span className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-primary">
          <Sun className="h-3.5 w-3.5" /> Verse of the Day
        </span>
        <p className="mt-3 font-serif text-2xl italic leading-snug text-foreground">"{verse.text}"</p>
        <cite className="mt-2 block font-sans text-xs uppercase not-italic tracking-[0.18em] text-primary">{verse.reference}</cite>
        {verse.reflection && <p className="mt-3 font-body text-sm leading-relaxed text-muted-foreground">{verse.reflection}</p>}
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <button
            data-testid="daily-verse-explore"
            onClick={() => onExplore(`What does ${verse.reference.replace("(KJV)", "").trim()} teach us?`)}
            className="inline-flex items-center gap-1.5 font-sans text-sm text-primary transition-colors hover:text-foreground"
          >
            Explore this verse <ArrowRight className="h-4 w-4" />
          </button>
          <button
            data-testid="daily-verse-card"
            onClick={() => setCardOpen(true)}
            className="inline-flex items-center gap-1.5 font-sans text-sm text-muted-foreground transition-colors hover:text-primary"
          >
            <ImageDown className="h-4 w-4" />
            Save card
          </button>
        </div>
        <CardDialog
          open={cardOpen}
          onOpenChange={setCardOpen}
          endpoint={`${API}/daily-verse/card.png`}
          filenamePrefix="askyeshua-verse-of-the-day"
          shareText={`"${verse.text}" — ${verse.reference} · AskYeshua`}
          shareUrl={`${API}/share/daily`}
        />
      </div>
    </motion.div>
  );
}
